import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Card, Typography, Drawer, Input, Descriptions, App, Tag, Alert, Segmented } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { ResidenceReviewListItem, ResidenceReviewStatus, ResidenceVisionCheck } from '@mygate/common';
import { residenceReviewService } from '@/services/residenceReview';
import { formatDateTime } from '@/utils/date';

const { Text, Title, Paragraph } = Typography;

// reviewedBy value the backend stamps on an AI auto-approval (MYG-978/982).
const AUTO_REVIEWER = 'system:residence-vision';

const checkTag = (label: string, value?: boolean) =>
  value === undefined ? null : (
    <Tag key={label} color={value ? 'green' : 'red'}>
      {value ? '✓' : '✗'} {label}
    </Tag>
  );

const statusTag = (r: ResidenceReviewListItem) => {
  const auto = r.reviewedBy === AUTO_REVIEWER;
  switch (r.reviewStatus) {
    case ResidenceReviewStatus.ACCEPTED:
      return <Tag color="green">{auto ? 'Akzeptiert (KI)' : 'Akzeptiert'}</Tag>;
    case ResidenceReviewStatus.REJECTED:
      return <Tag color="red">Abgelehnt</Tag>;
    case ResidenceReviewStatus.UNDER_REVIEW:
      return <Tag color="gold">Offen</Tag>;
    default:
      return <Tag>—</Tag>;
  }
};

const decidedBy = (r: ResidenceReviewListItem): string => (r.reviewedBy === AUTO_REVIEWER ? 'KI' : r.reviewedBy || '—');

// CSV export helpers (MYG-982). Escape a cell and trigger a client-side download.
const csvCell = (v: string | number | boolean | null | undefined): string => {
  const s = v === undefined || v === null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** Shows the STACKIT extraction result for staff. Informational only — the human still decides. */
function VisionPanel({ vc }: { vc: ResidenceVisionCheck }) {
  const statusMap = {
    EXTRACTED: ['blue', 'Extrahiert'],
    SKIPPED: ['orange', 'Übersprungen'],
    ERROR: ['red', 'Fehler'],
  } as const;
  const [color, statusLabel] = statusMap[vc.status];
  const a = vc.extracted?.address;
  const addressLine =
    (a &&
      [[a.street, a.houseNumber].filter(Boolean).join(' '), [a.postalCode, a.city].filter(Boolean).join(' '), a.country]
        .filter(Boolean)
        .join(', ')) ||
    '—';
  const c = vc.checks ?? {};
  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space wrap>
          KI-Auswertung (STACKIT) <Tag color={color}>{statusLabel}</Tag>
          {typeof vc.confidence === 'number' && <Tag>Konfidenz {Math.round(vc.confidence * 100)}%</Tag>}
        </Space>
      }
    >
      {vc.status === 'EXTRACTED' ? (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Name (Dokument)">{vc.extracted?.fullName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Adresse">{addressLine}</Descriptions.Item>
            <Descriptions.Item label="Ausstellungsdatum">{vc.extracted?.issueDate || '—'}</Descriptions.Item>
            <Descriptions.Item label="Aussteller">{vc.extracted?.issuer || '—'}</Descriptions.Item>
            <Descriptions.Item label="Dokumenttyp">{vc.extracted?.documentType || '—'}</Descriptions.Item>
          </Descriptions>
          <Space wrap style={{ marginTop: 12 }}>
            {checkTag('Adresse vollständig', c.addressComplete)}
            {checkTag('DE-Adresse', c.isGermanAddress)}
            {checkTag('Typ akzeptiert', c.documentTypeAccepted)}
            {checkTag('Aktuell', c.isFresh)}
            {checkTag('Authentisch', c.looksAuthentic)}
            {checkTag('Name ↔ KYC', vc.nameMatch)}
          </Space>
          {vc.reasoning && (
            <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
              {vc.reasoning}
            </Paragraph>
          )}
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Nur zur Unterstützung — die Entscheidung trifft der Mensch.
          </Text>
        </>
      ) : (
        <Alert
          type={vc.status === 'ERROR' ? 'error' : 'warning'}
          showIcon
          message={vc.status === 'ERROR' ? 'KI-Fehler bei der Auswertung' : 'KI hat das Dokument übersprungen'}
          description={vc.error || vc.note || '—'}
        />
      )}
    </Card>
  );
}

/**
 * Post-KYC residence document review (MYG-341). The internal support pipeline: staff open a queued
 * Meldebescheinigung, compare it against the KYC-confirmed name, and ACCEPT (unlocks the app for the
 * user) or REJECT with a reason (the user is sent back to re-upload).
 */
export function ResidenceReviewPage() {
  const { message, modal } = App.useApp();
  const [items, setItems] = useState<ResidenceReviewListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ResidenceReviewListItem | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docIsPdf, setDocIsPdf] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);
  const [scope, setScope] = useState<'pending' | 'all'>('pending');
  const [rechecking, setRechecking] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await residenceReviewService.listReviews(scope, { page, limit: pageSize, search });
      setItems(data?.items ?? []);
      setTotal(data?.total ?? data?.items?.length ?? 0);
    } catch {
      message.error('Fehler beim Laden der Liste');
    } finally {
      setLoading(false);
    }
  }, [message, scope, page, pageSize, search]);

  // load depends on scope/page/pageSize/search, so this refetches whenever any of them change.
  useEffect(() => {
    void load();
  }, [load]);

  // Changing the scope or submitting a search resets to the first page; load then refetches.
  const changeScope = (next: 'pending' | 'all') => {
    setScope(next);
    setPage(1);
  };
  const submitSearch = (value: string) => {
    setSearch(value.trim());
    setPage(1);
  };

  const openReview = async (item: ResidenceReviewListItem) => {
    setSelected(item);
    setRejectReason('');
    setDocUrl(null);
    try {
      const blob = await residenceReviewService.getDocument(item.userId);
      setDocIsPdf(blob.type === 'application/pdf');
      setDocUrl(URL.createObjectURL(blob));
    } catch {
      message.error('Dokument konnte nicht geladen werden');
    }
  };

  const closeDrawer = () => {
    if (docUrl) URL.revokeObjectURL(docUrl);
    setDocUrl(null);
    setSelected(null);
  };

  const decide = async (decision: 'ACCEPT' | 'REJECT') => {
    if (!selected) return;
    if (decision === 'REJECT' && !rejectReason.trim()) {
      message.warning('Bitte einen Ablehnungsgrund angeben');
      return;
    }
    setActing(true);
    try {
      await residenceReviewService.review(selected.userId, {
        decision,
        reason: decision === 'REJECT' ? rejectReason.trim() : undefined,
      });
      message.success(decision === 'ACCEPT' ? 'Dokument akzeptiert' : 'Dokument abgelehnt');
      closeDrawer();
      void load();
    } catch {
      message.error('Aktion fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const confirmAccept = () => {
    modal.confirm({
      title: 'Wohnsitznachweis akzeptieren?',
      content: 'Der Name wurde mit dem KYC-Ergebnis abgeglichen. Die App wird für den Kunden freigeschaltet.',
      okText: 'Akzeptieren',
      cancelText: 'Abbrechen',
      onOk: () => decide('ACCEPT'),
    });
  };

  // MYG-982: re-run the STACKIT check. The job is async, so reload shortly after to show the new result.
  const recheck = async (userId: string) => {
    setRechecking(userId);
    try {
      await residenceReviewService.recheck(userId);
      message.success('KI-Prüfung neu gestartet');
      setTimeout(() => void load(), 2500);
    } catch {
      message.error('KI-Prüfung konnte nicht gestartet werden');
    } finally {
      setRechecking(null);
    }
  };

  // Export the current filter (scope + search) as CSV. Fetches up to 500 matching rows, not just the page.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const data = await residenceReviewService.listReviews(scope, { search, limit: 500, page: 1 });
      const rows = data?.items ?? [];
      const header = [
        'userId',
        'firstName',
        'lastName',
        'status',
        'reviewedBy',
        'confidence',
        'nameMatch',
        'uploadedAt',
        'decidedAt',
      ];
      const lines = rows.map(r =>
        [
          r.userId,
          r.firstName,
          r.lastName,
          r.reviewStatus,
          r.reviewedBy,
          r.visionCheck?.confidence,
          r.visionCheck?.nameMatch,
          r.lockedAt ? new Date(r.lockedAt).toISOString() : '',
          r.reviewedAt ? new Date(r.reviewedAt).toISOString() : '',
        ]
          .map(csvCell)
          .join(','),
      );
      downloadCsv(
        [header.join(','), ...lines].join('\n'),
        `residence-reviews-${scope}-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      message.success(`${rows.length} Zeilen exportiert`);
    } catch {
      message.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<ResidenceReviewListItem> = [
    {
      title: 'Name (laut KYC)',
      key: 'name',
      render: (_, r) => <Text strong>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</Text>,
    },
    { title: 'User-ID', dataIndex: 'userId', key: 'userId', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Hochgeladen', dataIndex: 'lockedAt', key: 'lockedAt', render: (v: string) => formatDateTime(v) },
    { title: 'Status', key: 'status', render: (_, r) => statusTag(r) },
    {
      title: 'KI',
      key: 'ai',
      render: (_, r) => {
        const vc = r.visionCheck;
        if (!vc) return <Tag>—</Tag>;
        const map = {
          EXTRACTED: ['blue', 'extrahiert'],
          SKIPPED: ['orange', 'übersprungen'],
          ERROR: ['red', 'Fehler'],
        } as const;
        const [color, label] = map[vc.status];
        return (
          <Space size={4}>
            <Tag color={color}>{label}</Tag>
            {typeof vc.confidence === 'number' && <Text type="secondary">{Math.round(vc.confidence * 100)}%</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Entschieden',
      key: 'decided',
      render: (_, r) =>
        r.reviewedAt ? (
          <Space direction="vertical" size={0}>
            <Text>{decidedBy(r)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDateTime(r.reviewedAt as unknown as string)}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Aktion',
      key: 'action',
      render: (_, r) => (
        <Space>
          <Button type="primary" onClick={() => openReview(r)}>
            Prüfen
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={rechecking === r.userId}
            disabled={!r.documentFileId}
            onClick={() => void recheck(r.userId)}
          >
            KI neu
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          Wohnsitznachweise — Prüfung
        </Title>
        <Space wrap>
          <Segmented
            value={scope}
            onChange={v => changeScope(v as 'pending' | 'all')}
            options={[
              { label: 'Offen', value: 'pending' },
              { label: 'Alle (letzte)', value: 'all' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Name oder User-ID"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onSearch={submitSearch}
            style={{ width: 240 }}
          />
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportCsv()}>
            Export (CSV)
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>
            Aktualisieren
          </Button>
        </Space>
      </Space>

      <Table<ResidenceReviewListItem>
        rowKey="userId"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: t => `${t} Einträge`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <Drawer
        title="Wohnsitznachweis prüfen"
        open={!!selected}
        onClose={closeDrawer}
        width={640}
        extra={
          <Space>
            <Button danger icon={<CloseCircleOutlined />} loading={acting} onClick={() => decide('REJECT')}>
              Ablehnen
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={acting} onClick={confirmAccept}>
              Akzeptieren
            </Button>
          </Space>
        }
      >
        {selected && (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Name (laut KYC)">
                {[selected.firstName, selected.lastName].filter(Boolean).join(' ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="User-ID">
                <Text code>{selected.userId}</Text>
              </Descriptions.Item>
            </Descriptions>

            {selected.visionCheck ? (
              <VisionPanel vc={selected.visionCheck} />
            ) : (
              <Alert
                type="info"
                showIcon
                message="KI-Auswertung ausstehend oder nicht verfügbar"
                style={{ marginBottom: 16 }}
              />
            )}

            <Text type="secondary">Name im Dokument mit dem KYC-Namen oben abgleichen:</Text>
            <div
              style={{
                marginTop: 8,
                marginBottom: 16,
                border: '1px solid #303030',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {!docUrl ? (
                <div style={{ padding: 24, textAlign: 'center' }}>Dokument wird geladen…</div>
              ) : docIsPdf ? (
                <iframe title="document" src={docUrl} style={{ width: '100%', height: 480, border: 0 }} />
              ) : (
                <img alt="Wohnsitznachweis" src={docUrl} style={{ width: '100%', display: 'block' }} />
              )}
            </div>

            <Text>Ablehnungsgrund (nur bei Ablehnung — wird dem Kunden angezeigt):</Text>
            <Input.TextArea
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="z. B. Name stimmt nicht überein / Dokument älter als 6 Monate / kein Meldebescheinigung"
              style={{ marginTop: 8 }}
            />
          </>
        )}
      </Drawer>
    </Card>
  );
}
